import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

const PinRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 52px);
  gap: 8px;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 6px;
  }
`;

const PinBox = styled.input`
  width: 100%;
  min-width: 0;
  height: 60px;
  box-sizing: border-box;
  border-radius: 8px;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  outline: none;
  caret-color: transparent;
  font-family: ${(props) =>
      props.theme?.fonts?.heading?.fontFamily || 'Barlow Condensed'},
    -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial,
    sans-serif;

  @media (max-width: 768px) {
    height: 56px;
    font-size: 25px;
  }

  @media (max-width: 760px) {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.22);
  }

  &:focus {
    border-color: #fa4616;
  }

  @media (max-width: 760px) {
    &:focus {
      box-shadow: 0 0 0 2px rgba(250, 70, 22, 0.2);
    }
  }

  ${(props) =>
    props.$variant === 'split'
      ? css`
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          font-size: 30px;
          line-height: 1.2;

          @media (max-width: 768px) {
            font-size: 31px;
          }
        `
      : ''}

  ${(props) =>
    props.$variant === 'large'
      ? css`
          font-size: 30px;
          line-height: 1.2;

          @media (max-width: 768px) {
            font-size: 31px;
          }
        `
      : ''}
`;

const OtpInput = ({
  values,
  onChange,
  onComplete,
  variant,
  autoFocusOnMount = true,
}) => {
  const inputRefs = useRef([]);
  const hasAutoFocused = useRef(false);

  useEffect(() => {
    if (!autoFocusOnMount || hasAutoFocused.current) {
      return;
    }

    const firstEmptyIndex = values.findIndex((value) => !value);
    const focusIndex = firstEmptyIndex === -1 ? 0 : firstEmptyIndex;
    const targetInput = inputRefs.current[focusIndex];

    if (targetInput) {
      targetInput.focus();
      hasAutoFocused.current = true;
    }
  }, [autoFocusOnMount, values]);

  const handleFocus = (event) => {
    event.target.select();
    const inputNode = event.target;
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 900px)').matches
    ) {
      window.setTimeout(() => {
        inputNode.scrollIntoView({
          block: 'center',
          inline: 'nearest',
          behavior: 'auto',
        });
      }, 120);
    }
  };

  const handleChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, '');

    if (raw.length > 1) {
      const code = raw.slice(0, 6);
      const newValues = [...values];
      for (let i = 0; i < 6; i++) {
        newValues[i] = code[i] || '';
      }
      onChange(newValues);

      const focusIndex = Math.min(code.length, 5);
      inputRefs.current[focusIndex].focus();

      if (newValues.every((v) => v !== '')) {
        onComplete(newValues);
      }
      return;
    }

    const val = raw.slice(-1);

    const newValues = [...values];
    newValues[index] = val;
    onChange(newValues);

    if (val && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    if (val && newValues.every((v) => v !== '')) {
      onComplete(newValues);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!values[index] && index > 0) {
        const newValues = [...values];
        newValues[index - 1] = '';
        onChange(newValues);
        inputRefs.current[index - 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (pasted.length > 0) {
      const newValues = [...values];
      for (let i = 0; i < 6; i++) {
        newValues[i] = pasted[i] || '';
      }
      onChange(newValues);

      const focusIndex = Math.min(pasted.length, 5);
      inputRefs.current[focusIndex].focus();

      if (newValues.every((v) => v !== '')) {
        onComplete(newValues);
      }
    }
  };

  return (
    <PinRow>
      {values.map((val, i) => (
        <PinBox
          key={i}
          $variant={variant}
          ref={(el) => {
            inputRefs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={val}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={handleFocus}
        />
      ))}
    </PinRow>
  );
};

export default OtpInput;
